'use client';

import { useEffect, useRef, useState } from 'react';
import ParticleCanvas from '../home/components/ParticleCanvas';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TermsPage() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => {
      if (sectionRef?.current) {
        observer?.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <>
      <ParticleCanvas />
      <div className="vignette" aria-hidden="true" />
      <Header />
      <main className="relative min-h-screen pt-32 pb-20 px-6" style={{ zIndex: 2 }}>
        <div 
          ref={sectionRef}
          className={`max-w-[800px] mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Hero Title */}
          <h1 
            className="text-5xl md:text-6xl font-black text-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #c4beff 50%, #6a5cff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Terms of Service
          </h1>

          {/* Subtitle */}
          <p className="text-center text-vanta-muted text-lg mb-16">
            Terms and conditions for using VantaRust services.
          </p>

          {/* Content Card */}
          <div 
            className="rounded-3xl p-8 md:p-12"
            style={{
              background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.08) 0%, rgba(139, 127, 255, 0.05) 100%)',
              border: '2px solid rgba(106, 92, 255, 0.2)',
              backdropFilter: 'blur(30px)',
              boxShadow: '0 25px 80px rgba(106, 92, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="space-y-8 text-vanta-text">
              {/* Last Updated */}
              <p className="text-sm text-vanta-muted italic">
                Last updated: February 22, 2026
              </p>

              <p className="leading-relaxed">
                These Terms of Service ("Terms") govern your access to and use of the VantaRust website, Rust game servers, and related services (collectively, the "Services").
              </p>

              <p className="leading-relaxed">
                By accessing or using our Services, you agree to be bound by these Terms.
              </p>

              <p className="leading-relaxed font-semibold">
                If you do not agree, you must not use our Services.
              </p>

              {/* 1. Operator */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">1. Operator</h2>
                <p className="leading-relaxed mb-3">
                  The Services are operated by:
                </p>
                <div className="pl-4 space-y-1">
                  <p className="leading-relaxed">VantaRust</p>
                  <p className="leading-relaxed">Email: support@vantarust.com</p>
                  <p className="leading-relaxed">Switzerland</p>
                </div>
              </section>

              {/* 2. Description of Service */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
                <p className="leading-relaxed mb-3">
                  VantaRust provides privately operated game servers for the video game Rust, as well as related website and community services.
                </p>
                <p className="leading-relaxed mb-3">
                  We reserve the right to modify, suspend, or discontinue any part of the Services at any time without notice.
                </p>
                <p className="leading-relaxed">
                  We do not guarantee continuous availability or error-free operation.
                </p>
              </section>

              {/* 3. Eligibility */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. Eligibility</h2>
                <p className="leading-relaxed mb-3">
                  You must comply with:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>These Terms</li>
                  <li>All applicable laws</li>
                  <li>Steam Terms of Service</li>
                  <li>Facepunch Studios Terms</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  You are responsible for your Steam account and any activity under it.
                </p>
              </section>

              {/* 4. Accounts and Player Data */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. Accounts and Player Data</h2>
                <p className="leading-relaxed mb-3">
                  When using our Services, we may collect and store:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Steam ID</li>
                  <li>Username</li>
                  <li>IP address</li>
                  <li>Gameplay data</li>
                  <li>Chat messages</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  This is required for server functionality, security, and rule enforcement.
                </p>
              </section>

              {/* 5. Server Rules and Enforcement */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Server Rules and Enforcement</h2>
                <p className="leading-relaxed mb-3">
                  We reserve the absolute right to:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Remove players</li>
                  <li>Reset player progress</li>
                  <li>Wipe servers</li>
                  <li>Restrict access</li>
                  <li>Permanently ban users</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  at our sole discretion.
                </p>
                <p className="leading-relaxed mt-3 mb-3">
                  Reasons may include, but are not limited to:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Cheating</li>
                  <li>Exploiting</li>
                  <li>Abuse</li>
                  <li>Harassment</li>
                  <li>Disruptive behavior</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  We are not obligated to provide warnings or explanations.
                </p>
                <p className="leading-relaxed font-semibold">
                  All enforcement decisions are final.
                </p>
              </section>

              {/* 6. Virtual Property */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Virtual Property</h2>
                <p className="leading-relaxed mb-3">
                  All virtual items, ranks, currencies, and data:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Are part of the service</li>
                  <li>Have no real-world value</li>
                  <li>Remain the property of VantaRust</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  You have no ownership rights.
                </p>
                <p className="leading-relaxed">
                  We may modify or delete virtual data at any time.
                </p>
                <p className="leading-relaxed">
                  This includes wipes and resets.
                </p>
              </section>

              {/* 7. Payments, Donations, and Purchases */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Payments, Donations, and Purchases</h2>
                <p className="leading-relaxed mb-3">
                  Payments made to VantaRust are voluntary and support server operation.
                </p>
                <p className="leading-relaxed mb-3">
                  Payments do not purchase real-world goods.
                </p>
                <p className="leading-relaxed mb-3">
                  We reserve the right to modify or remove purchased features at any time.
                </p>
                <p className="leading-relaxed mb-3">
                  All payments are final.
                </p>
                <p className="leading-relaxed mb-3">
                  To the maximum extent permitted by law:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>No refunds will be provided</li>
                  <li>No guarantees are made regarding service duration</li>
                </ul>
              </section>

              {/* 8. Chargebacks and Payment Disputes */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Chargebacks and Payment Disputes</h2>
                <p className="leading-relaxed mb-3">
                  If you initiate a chargeback or dispute:
                </p>
                <p className="leading-relaxed mb-3">
                  We reserve the right to:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Permanently ban your account</li>
                  <li>Revoke all virtual items</li>
                  <li>Restrict access to Services</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Fraudulent disputes may be reported.
                </p>
              </section>

              {/* 9. Service Availability */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. Service Availability</h2>
                <p className="leading-relaxed mb-3">
                  We do not guarantee:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Server uptime</li>
                  <li>Data preservation</li>
                  <li>Continuous availability</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Servers may be reset, wiped, or discontinued at any time.
                </p>
                <p className="leading-relaxed">
                  We are not liable for data loss.
                </p>
              </section>

              {/* 10. Acceptable Use */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">10. Acceptable Use</h2>
                <p className="leading-relaxed mb-3">
                  You agree not to:
                </p>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Use cheats or exploits</li>
                  <li>Attempt to hack or disrupt Services</li>
                  <li>Harass other users</li>
                  <li>Interfere with server operation</li>
                  <li>Attempt unauthorized access</li>
                  <li>Violate any applicable laws</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}